/**********************************************************************
 * src/components/forms/AddPerson.jsx
 *********************************************************************/
import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate }            from 'react-router-dom'
import Select                     from 'react-select'
import api                        from '../../services/api'
import Loader                     from '../Utils/Loader'
import { formatImageBytes, normalizePersonImage } from '../../utils/normalizePersonImage'
import '../../css/addPerson.css'

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */
const AddPerson = () => {
  const navigate = useNavigate()

  /* ---------- opzioni qualifiche ---------------------------------- */
  const [qualificationOptions, setQualificationOptions] = useState([])

  /* ---------- state form ----------------------------------------- */
  const [formData, setFormData] = useState({
    nome           : '',
    cognome        : '',
    data_nascita   : '',
    sesso          : '',
    description    : '',
    phone          : '',
    email          : '',
    qualifications : [],
    imageBase64    : '',
  })

  const [preview,         setPreview]         = useState(null)
  const [isImageLoading,  setIsImageLoading]  = useState(false)
  const [error,           setError]           = useState('')
  const [success,         setSuccess]         = useState('')
  const [isSubmitting,    setIsSubmitting]    = useState(false)
  const [imageInfo,       setImageInfo]       = useState(null)

  /* ---------- today (YYYY-MM-DD) – usato come max nell’input ------ */
  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])

  /* ---------- bootstrap opzioni qualifiche ------------------------ */
  useEffect(() => {
    api.get('/qualifications')
      .then(res => {
        const opts = res.data
          .map(q => ({ value: q.id, label: q.name }))
          .sort((a, b) => a.label.localeCompare(b.label))
        setQualificationOptions(opts)
      })
      .catch(err => console.error('Errore nel recupero delle qualifiche:', err))
  }, [])

  /* ------------------ handlers ------------------------------------ */
  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleQualificationSelect = selected => {
    const values = Array.isArray(selected)
      ? selected.map(o => o.value)
      : selected
        ? [selected.value]
        : []
    setFormData({ ...formData, qualifications: values })
  }

  const handleFileChange = async e => {
    const file = e.target.files[0]
    if (!file) {
      setPreview(null)
      setImageInfo(null)
      setFormData(prev => ({ ...prev, imageBase64: '' }))
      return
    }

    setError('')
    setIsImageLoading(true)
    try {
      const normalized = await normalizePersonImage(file)
      setPreview(normalized.dataUrl)
      setImageInfo(normalized)
      setFormData(prev => ({ ...prev, imageBase64: normalized.dataUrl }))
    } catch (err) {
      console.error('Errore nella normalizzazione della foto:', err)
      setPreview(null)
      setImageInfo(null)
      setFormData(prev => ({ ...prev, imageBase64: '' }))
      setError(err?.message || 'Impossibile preparare questa immagine.')
      e.target.value = ''
    } finally {
      setIsImageLoading(false)
    }
  }

  /* ------------------ submit -------------------------------------- */
  const handleSubmit = async e => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (isImageLoading || isSubmitting) return

    /* blocco DOB futura anche lato-client */
    if (
      formData.data_nascita &&
      formData.data_nascita > today               // stringhe YYYY-MM-DD
    ) {
      setError('La data di nascita non può essere nel futuro.')
      return
    }

    setIsSubmitting(true)
    try {
      await api.post('/people', formData, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 45000,
      })
      setSuccess('Persona aggiunta con successo!')
      /* reset form */
      setFormData({
        nome           : '',
        cognome        : '',
        data_nascita   : '',
        sesso          : '',
        description    : '',
        phone          : '',
        email          : '',
        qualifications : [],
        imageBase64    : '',
      })
      setPreview(null)
      setImageInfo(null)
    } catch (err) {
      console.error("Errore nell'aggiunta della persona:", err)
      const msg = err?.code === 'ECONNABORTED'
        ? 'Il caricamento ha impiegato troppo tempo. Riprova senza chiudere la pagina.'
        : err?.response?.data?.error || "Errore nell'aggiunta della persona"
      setError(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  /* ----------------------------------------------------------------*/
  /* Render                                                           */
  /* ----------------------------------------------------------------*/
  return (
    <div className="addperson-container">
      <h2>Aggiungi Persona</h2>

      {error   && <p className="error">{error}</p>}
      {success && <p className="success">{success}</p>}

      <form onSubmit={handleSubmit} className="addperson-form">
        {/* Nome ----------------------------------------------------- */}
        <label>
          Nome:
          <input
            type="text"
            name="nome"
            value={formData.nome}
            onChange={handleChange}
            required
            placeholder="Inserisci il nome"
          />
        </label>

        {/* Cognome -------------------------------------------------- */}
        <label>
          Cognome:
          <input
            type="text"
            name="cognome"
            value={formData.cognome}
            onChange={handleChange}
            required
            placeholder="Inserisci il cognome"
          />
        </label>

        {/* Telefono ------------------------------------------------- */}
        <label>
          Telefono:
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Inserisci il numero di telefono"
          />
        </label>

        {/* Email ---------------------------------------------------- */}
        <label>
          Email:
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Inserisci l'indirizzo email"
          />
        </label>

        {/* Data di nascita (max = today) --------------------------- */}
        <label>
          Data di nascita:
          <input
            type="date"
            name="data_nascita"
            value={formData.data_nascita}
            onChange={handleChange}
            max={today}                 /* ← BLOCCO date future */
          />
        </label>

        {/* Sesso ---------------------------------------------------- */}
        <label>
          Sesso:
          <select
            name="sesso"
            value={formData.sesso}
            onChange={handleChange}
            required
          >
            <option value="">Seleziona</option>
            <option value="M">Maschile</option>
            <option value="F">Femminile</option>
            <option value="Altro">Altro</option>
          </select>
        </label>

        {/* Qualifiche ---------------------------------------------- */}
        <label>
          Qualifiche:
          <Select
            isMulti
            name="qualifications"
            options={qualificationOptions}
            onChange={handleQualificationSelect}
            placeholder="Seleziona qualifiche..."
            isClearable
            classNamePrefix="select"
            value={qualificationOptions.filter(opt =>
              formData.qualifications.includes(opt.value)
            )}
          />
        </label>

        {/* Upload immagine ----------------------------------------- */}
        <label>
          Upload Image:
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={isImageLoading || isSubmitting}
          />
        </label>
        <div className="preview-container">
          {isImageLoading ? <Loader /> : (preview && <img src={preview} alt="Anteprima" />)}
        </div>
        {imageInfo && (
          <small>
            Immagine ottimizzata: {imageInfo.width}×{imageInfo.height}, {' '}
            {formatImageBytes(imageInfo.originalBytes)} → {formatImageBytes(imageInfo.outputBytes)}
          </small>
        )}

        {/* Descrizione --------------------------------------------- */}
        <label>
          Description:
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Inserisci una breve descrizione..."
          />
        </label>

        <button type="submit" disabled={isImageLoading || isSubmitting}>
          {isSubmitting ? <Loader /> : 'Aggiungi Persona'}
        </button>
      </form>

      {success && (
        <button onClick={() => navigate('/dashboard')} className="redirect-btn">
          Vai alla Dashboard
        </button>
      )}
    </div>
  )
}

export default AddPerson
